import Phaser from 'phaser';

import { LottieCanvasSprite } from '@/features/laneChooserPhaser/game/objects/LottieCanvasSprite';
import { computeBottomLaneCenters } from '@/features/laneChooserPhaser/game/utils/laneLayout';
import { getCharacterJumpHeightPx, getResponsiveWidthPx } from '@/features/laneChooserPhaser/game/utils/responsive';

import type { LaneChooserAnswers, LaneChooserPlayer } from '@/features/laneChooserPhaser/game/types';
import type { LaneShapeMetrics } from '@/features/laneChooserPhaser/game/utils/laneShape';

type PlayerControllerConfig = {
	scene: Phaser.Scene;
	container: Phaser.GameObjects.Container;
	laneMetrics: LaneShapeMetrics;
	laneLinesCount: number;
	laneBottomPct: number;
	viewportWidth: number;
	renderScaleX: number;
	renderScaleY: number;
	themeConfig: LaneChooserPlayer;
	answerBoxWidths: LaneChooserAnswers['widthPx'];
	lottieSourceRoot: HTMLElement;
};

export class PlayerController {
	// Scene reference used for animation creation and tweens.
	private readonly scene: Phaser.Scene;
	// Parent HUD container that keeps the player above the world layer.
	private readonly container: Phaser.GameObjects.Container;
	// Shared lane metrics used to keep the player aligned to the rendered lane.
	private laneMetrics: LaneShapeMetrics;
	// Theme-owned player assets, sizes, and timings.
	private readonly themeConfig: LaneChooserPlayer;
	// Number of visible lanes used to compute horizontal targets.
	private laneLinesCount: number;
	// Lane bottom width percentage used by the legacy center-alignment helper.
	private laneBottomPct: number;
	// Original CSS viewport width passed down from React.
	private viewportWidth: number;
	// X-axis multiplier from CSS pixels into Phaser render pixels.
	private renderScaleX: number;
	// Y-axis multiplier from CSS pixels into Phaser render pixels.
	private renderScaleY: number;
	// Responsive answer-box widths used as the source of truth for player sizing.
	private readonly answerBoxWidths: LaneChooserAnswers['widthPx'];
	// Root container that receives horizontal lane movement and jump transforms.
	private readonly root: Phaser.GameObjects.Container;
	// Live Phaser texture backed by the default Lottie JSON animation.
	private readonly defaultAnimation: LottieCanvasSprite;
	// Live Phaser texture backed by the one-shot correct Lottie JSON animation.
	private readonly correctAnimation: LottieCanvasSprite;
	// Static jump sprite that replaces the animated sprite during jump motion.
	private readonly jumpSprite: Phaser.GameObjects.Image;
	// Currently visible player visual used to coordinate playback state.
	private activeVisual: 'default' | 'correct' | 'jump' = 'default';
	// Cached bottom lane centers used as the movement targets.
	private laneCenters: number[];
	// Current target lane index.
	private laneIndex: number;
	// Active horizontal movement tween so repeated input can retarget cleanly.
	private moveTween: Phaser.Tweens.Tween | null = null;
	// Active vertical jump tween for the current half of the jump.
	private jumpTween: Phaser.Tweens.Tween | null = null;
	// Base Y position that the player returns to after jump motion finishes.
	private baseY = 0;
	// Tracks whether the player is currently in the jump state.
	private isJumping = false;
	// Tracks whether the current jump is in its downward half.
	private isDescending = false;
	// Tracks whether idle playback should run at boosted speed.
	private isBoosting = false;
	// Mirrors the legacy "answers waiting at the player" state to freeze idle playback.
	private waitingAtBottom = false;
	// Queues the correct celebration until the jump finishes, matching the legacy flow.
	private pendingCorrectAnimation = false;
	// Prevents work after the controller has been destroyed.
	private destroyed = false;

	constructor({
		scene,
		container,
		laneMetrics,
		laneLinesCount,
		laneBottomPct,
		viewportWidth,
		renderScaleX,
		renderScaleY,
		themeConfig,
		answerBoxWidths,
		lottieSourceRoot,
	}: PlayerControllerConfig) {
		this.scene = scene;
		this.container = container;
		this.laneMetrics = laneMetrics;
		this.laneLinesCount = laneLinesCount;
		this.laneBottomPct = laneBottomPct;
		this.viewportWidth = viewportWidth;
		this.renderScaleX = renderScaleX;
		this.renderScaleY = renderScaleY;
		this.themeConfig = themeConfig;
		this.answerBoxWidths = answerBoxWidths;
		this.laneCenters = this.computeLaneCenters();
		this.laneIndex = this.getInitialLaneIndex();

		this.root = this.scene.add.container(0, 0);
		this.defaultAnimation = new LottieCanvasSprite({
			scene: this.scene,
			parent: this.root,
			sourceRoot: lottieSourceRoot,
			textureKey: this.themeConfig.animations.default.textureKey,
			animationData: this.themeConfig.animations.default.animationData,
			canvasWidth: this.themeConfig.animations.default.canvasWidth,
			canvasHeight: this.themeConfig.animations.default.canvasHeight,
			loop: true,
		});
		this.correctAnimation = new LottieCanvasSprite({
			scene: this.scene,
			parent: this.root,
			sourceRoot: lottieSourceRoot,
			textureKey: this.themeConfig.animations.correct.textureKey,
			animationData: this.themeConfig.animations.correct.animationData,
			canvasWidth: this.themeConfig.animations.correct.canvasWidth,
			canvasHeight: this.themeConfig.animations.correct.canvasHeight,
			loop: false,
			onComplete: () => this.playDefaultAnimation(),
		});
		this.jumpSprite = this.scene.add.image(0, 0, this.themeConfig.jump.textureKey);
		this.jumpSprite.setOrigin(0.5, 1);
		this.jumpSprite.setVisible(false);

		this.root.add(this.jumpSprite);
		this.container.add(this.root);

		this.syncLayout(false);
		this.playDefaultAnimation();
	}

	// Refreshes the visible live Lottie texture with its latest canvas frame.
	update() {
		this.defaultAnimation.update();
		this.correctAnimation.update();
	}

	// Releases tweens and destroys the root player container.
	destroy() {
		if (this.destroyed) {
			return;
		}

		this.destroyed = true;
		this.moveTween?.stop();
		this.moveTween = null;
		this.stopJumpTween();
		this.isDescending = false;
		this.pendingCorrectAnimation = false;
		this.defaultAnimation.destroy();
		this.correctAnimation.destroy();
		this.root.destroy();
	}

	// Switches the player to the non-looping correct animation and returns to idle once it finishes.
	playCorrectAnimation() {
		if (this.destroyed) {
			return;
		}

		if (this.isJumping) {
			this.pendingCorrectAnimation = true;
			return;
		}

		this.pendingCorrectAnimation = false;
		this.showCorrectAnimation();
		this.correctAnimation.setSpeed(1);
		this.correctAnimation.playFromStart();
	}

	// Starts the jump state with the legacy height, rotation, and landing behavior.
	jump(): boolean {
		if (this.destroyed || this.isJumping || !this.root.active) {
			return false;
		}

		const jumpHeightPx = getCharacterJumpHeightPx(this.viewportWidth) * this.renderScaleY;
		const jumpDurationMs = Math.max(100, this.themeConfig.jump.durationMs ?? 1600);
		const halfDurationMs = jumpDurationMs / 2;
		const jumpRotationDeg = this.themeConfig.jump.rotationDeg ?? 90;
		const jumpScale = this.themeConfig.jump.scale ?? 0.9;

		this.isJumping = true;
		this.isDescending = false;
		this.stopJumpTween();
		this.showJumpSprite();
		this.root.setAngle(0);
		this.root.setScale(1);

		this.jumpTween = this.scene.tweens.add({
			targets: this.root,
			y: this.baseY - jumpHeightPx,
			angle: jumpRotationDeg,
			scaleX: jumpScale,
			scaleY: jumpScale,
			duration: halfDurationMs,
			ease: 'Power1.out',
			onComplete: () => {
				this.isDescending = true;
				this.jumpTween = this.scene.tweens.add({
					targets: this.root,
					y: this.baseY,
					angle: 0,
					scaleX: 1,
					scaleY: 1,
					duration: halfDurationMs,
					ease: 'Power1.in',
					onComplete: () => {
						this.stopJumpTween();
						this.isJumping = false;
						this.isDescending = false;
						this.root.setY(this.baseY);
						this.root.setAngle(0);
						this.root.setScale(1);

						if (this.pendingCorrectAnimation) {
							this.playCorrectAnimation();
							return;
						}

						this.playDefaultAnimation();
					},
				});
			},
		});

		return true;
	}

	// Moves one lane to the left when possible.
	moveLeft(): boolean {
		return this.moveBy(-1);
	}

	// Moves one lane to the right when possible.
	moveRight(): boolean {
		return this.moveBy(1);
	}

	// Returns the currently active lane index so scene-level answer flow can use the player's lane as selection.
	getLaneIndex(): number {
		return this.laneIndex;
	}

	// Returns the active lane center in world coordinates so the portrait camera can follow the player.
	getLaneCenterX(): number {
		return this.laneCenters[this.laneIndex] ?? this.laneMetrics.centerX;
	}

	// Reports whether the player is currently inside the jump animation state.
	isJumpingNow(): boolean {
		return this.isJumping;
	}

	// Reports whether the player is currently in the downward half of the jump.
	isDescendingNow(): boolean {
		return this.isJumping && this.isDescending;
	}

	// Adjusts idle playback speed to reflect normal versus boost motion.
	setBoosting(isBoosting: boolean) {
		this.isBoosting = isBoosting;
		this.syncAnimationSpeed();
	}

	// Pauses the idle player animation while the answer row is waiting for a jump.
	setWaitingAtBottom(isWaitingAtBottom: boolean) {
		if (this.destroyed || this.waitingAtBottom === isWaitingAtBottom) {
			return;
		}

		this.waitingAtBottom = isWaitingAtBottom;
		this.syncAnimationPlayback();
	}

	// Reuses the current player runtime when lane geometry changes between questions or after resize.
	setLaneLayout({
		laneMetrics,
		laneLinesCount,
		laneBottomPct,
		viewportWidth,
		renderScaleX,
		renderScaleY,
	}: {
		laneMetrics: LaneShapeMetrics;
		laneLinesCount: number;
		laneBottomPct: number;
		viewportWidth: number;
		renderScaleX: number;
		renderScaleY: number;
	}) {
		this.laneMetrics = laneMetrics;
		this.laneLinesCount = laneLinesCount;
		this.laneBottomPct = laneBottomPct;
		this.viewportWidth = viewportWidth;
		this.renderScaleX = renderScaleX;
		this.renderScaleY = renderScaleY;
		this.syncLayout(false);
	}

	// Recomputes the target lane and animates the player root to the next valid center.
	private moveBy(step: -1 | 1): boolean {
		const nextLaneIndex = Phaser.Math.Clamp(this.laneIndex + step, 0, Math.max(0, this.laneCenters.length - 1));
		if (nextLaneIndex === this.laneIndex) {
			return false;
		}

		this.laneIndex = nextLaneIndex;
		this.syncLayout(true);
		return true;
	}

	// Computes the bottom lane centers using the same helper the legacy React character used.
	private computeLaneCenters(): number[] {
		return computeBottomLaneCenters({
			width: this.laneMetrics.width,
			lanes: this.laneLinesCount,
			laneBottomPct: this.laneBottomPct,
		});
	}

	// Picks the same centered starting lane as the legacy React movement hook.
	private getInitialLaneIndex(): number {
		const safeLaneCount = Math.max(1, this.laneCenters.length);
		return safeLaneCount % 2 === 1 ? Math.floor(safeLaneCount / 2) : Math.min(safeLaneCount - 1, safeLaneCount / 2);
	}

	// Applies the current display size and either snaps or tweens the player to the active lane.
	private syncLayout(animate: boolean) {
		this.laneCenters = this.computeLaneCenters();
		this.laneIndex = Phaser.Math.Clamp(this.laneIndex, 0, Math.max(0, this.laneCenters.length - 1));

		const displayWidthPx =
			getResponsiveWidthPx(this.viewportWidth, this.answerBoxWidths) *
			this.themeConfig.answerBoxWidthScale *
			this.renderScaleX;
		const animatedDisplayHeightPx =
			(displayWidthPx * this.themeConfig.animations.default.canvasHeight) /
			this.themeConfig.animations.default.canvasWidth;
		const jumpDisplayHeightPx =
			(displayWidthPx * this.themeConfig.jump.assetSize.height) / this.themeConfig.jump.assetSize.width;
		const targetX = this.laneCenters[this.laneIndex] ?? this.laneMetrics.centerX;
		const targetY =
			this.laneMetrics.topOffset + this.laneMetrics.height - this.themeConfig.bottomInsetPx * this.renderScaleY;

		this.baseY = targetY;
		this.root.setDepth(20);
		this.defaultAnimation.setDisplaySize(displayWidthPx, animatedDisplayHeightPx);
		this.correctAnimation.setDisplaySize(displayWidthPx, animatedDisplayHeightPx);
		this.jumpSprite.setDisplaySize(displayWidthPx, jumpDisplayHeightPx);

		if (!animate) {
			this.moveTween?.stop();
			this.moveTween = null;
			this.root.setX(targetX);
			if (!this.isJumping) {
				this.root.setY(targetY);
			}
			return;
		}

		this.moveTween?.stop();
		this.moveTween = this.scene.tweens.add({
			targets: this.root,
			x: targetX,
			duration: this.themeConfig.laneSwitchDurationMs ?? 220,
			ease: 'Power1',
			onStart: () => {
				if (!this.isJumping) {
					this.root.setY(targetY);
				}
			},
			onComplete: () => {
				this.moveTween = null;
				if (!this.isJumping) {
					this.root.setY(targetY);
				}
			},
		});
	}

	// Stops the active jump tween without affecting horizontal movement tweens.
	private stopJumpTween() {
		this.jumpTween?.stop();
		this.jumpTween = null;
	}

	// Shows the looping default Lottie and hides the other player visuals.
	private showDefaultAnimation() {
		this.activeVisual = 'default';
		this.defaultAnimation.setVisible(true);
		this.correctAnimation.setVisible(false);
		this.correctAnimation.stop();
		this.jumpSprite.setVisible(false);
	}

	// Shows the one-shot correct Lottie and hides the other player visuals.
	private showCorrectAnimation() {
		this.activeVisual = 'correct';
		this.defaultAnimation.setVisible(false);
		this.defaultAnimation.stop();
		this.correctAnimation.setVisible(true);
		this.jumpSprite.setVisible(false);
	}

	// Shows the static jump sprite and hides both Lottie visuals for the duration of the jump.
	private showJumpSprite() {
		this.activeVisual = 'jump';
		this.defaultAnimation.stop();
		this.defaultAnimation.setVisible(false);
		this.correctAnimation.stop();
		this.correctAnimation.setVisible(false);
		this.jumpSprite.setVisible(true);
	}

	// Resumes the looping idle animation after temporary states such as correct celebration or jump.
	private playDefaultAnimation() {
		if (this.destroyed || this.isJumping) {
			return;
		}

		this.showDefaultAnimation();
		this.syncAnimationSpeed();
		if (this.waitingAtBottom) {
			this.defaultAnimation.stop();
			return;
		}

		this.defaultAnimation.playFromStart();
	}

	// Applies the correct idle playback speed for normal versus boosted movement.
	private syncAnimationSpeed() {
		if (this.destroyed) {
			return;
		}

		this.defaultAnimation.setSpeed(this.isBoosting ? this.themeConfig.boostAnimationTimeScale ?? 2 : 1);
	}

	// Matches the legacy Lottie stop/play behavior while answers wait at the player.
	private syncAnimationPlayback() {
		if (this.destroyed || this.isJumping || this.activeVisual !== 'default') {
			return;
		}

		if (this.waitingAtBottom) {
			this.defaultAnimation.stop();
			return;
		}

		this.defaultAnimation.play();
	}
}
